import { ArrowUpRight } from "@untitledui/icons"
import type { FC, HTMLAttributes } from "react"

import { FeaturedIcon } from "@/components/ui/featured-icon"
import { Link } from "@/components/ui/link"
import { cn } from "@/lib/utils"

interface StatCardProps {
  icon: FC<HTMLAttributes<HTMLOrSVGElement>>
  label: string
  value: number
  color?: "brand" | "gray" | "success" | "warning" | "error"
  /** When set, the whole tile becomes a link to the page behind the figure. */
  href?: string
}

const BASE =
  "border-secondary bg-primary flex flex-col gap-4 rounded-xl border p-5 shadow-xs"

// A single at-a-glance figure. Hierarchy lives in the number (display weight,
// tabular numerals for clean column scanning); the label and icon stay quiet.
// Given an href, the whole tile is a link — the corner arrow marks it as one,
// and it lifts to shadow-md on hover with a Byline Blue focus ring.
export function StatCard({
  icon,
  label,
  value,
  color = "gray",
  href,
}: StatCardProps) {
  const figure = (
    <>
      <div className="flex items-start justify-between gap-2">
        <FeaturedIcon icon={icon} color={color} theme="light" size="md" />
        {href && (
          <ArrowUpRight
            aria-hidden="true"
            className="text-fg-quaternary group-hover:text-fg-secondary size-4 shrink-0 transition"
          />
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-primary text-display-sm font-semibold tabular-nums">
          {value.toLocaleString()}
        </p>
        <p className="text-tertiary text-sm font-medium">{label}</p>
      </div>
    </>
  )

  if (href) {
    return (
      <Link
        href={href}
        aria-label={`${label}: ${value.toLocaleString()}`}
        className={cn(
          BASE,
          // ui/link adds a default ring; suppress it so the tile shows the one
          // Byline Blue focus outline the rest of the chrome uses.
          "group outline-focus-ring transition duration-150 ease-out hover:shadow-md focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2",
        )}
      >
        {figure}
      </Link>
    )
  }

  return <div className={BASE}>{figure}</div>
}
